/**
 * Tiny DOM helpers — the only place the renderer talks to the document API
 * imperatively. Keeps screen modules declarative + terse. No framework.
 */

type Attrs = Record<string, string | number | boolean | undefined | null>;
type Child = Node | string | null | undefined | false;

/** Create an element with attrs (class, text, data attrs, onclick handler). */
export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs & { onclick?: (e: MouseEvent) => void } = {},
  children: Child[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v == null || v === false) continue;
    if (k === "class") node.className = String(v);
    else if (k === "text") node.textContent = String(v);
    else if (k === "html") node.innerHTML = String(v);
    else if (k === "onclick") node.addEventListener("click", v as EventListener);
    else node.setAttribute(k, String(v));
  }
  for (const c of children) {
    if (c == null || c === false) continue;
    node.appendChild(typeof c === "string" ? document.createTextNode(c) : c);
  }
  return node;
}

/** Shorthand: a div with a class and children. */
export function div(cls: string, children: Child[] = []): HTMLDivElement {
  return el("div", { class: cls }, children);
}

/** Shorthand: a span with a class + text. */
export function span(cls: string, text: string | number): HTMLSpanElement {
  return el("span", { class: cls, text });
}

/** A button element. */
export function button(
  cls: string,
  text: string,
  onclick: () => void,
  opts: { disabled?: boolean; title?: string } = {},
): HTMLButtonElement {
  const b = el("button", {
    class: cls,
    text,
    type: "button",
    disabled: opts.disabled,
    title: opts.title,
  });
  if (!opts.disabled) b.addEventListener("click", onclick);
  return b;
}

/** Remove all children of a node. */
export function clear(node: Element): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

/** Mount children into a node, replacing existing content. */
export function mount(node: Element, ...children: Child[]): void {
  clear(node);
  for (const c of children) {
    if (c == null || c === false) continue;
    node.appendChild(typeof c === "string" ? document.createTextNode(c) : c);
  }
}
